import type { AttachedFile } from "./api";

export type PastedText = Extract<AttachedFile, { type: "paste" }>;

// Same cut-off Claude Code uses before collapsing a paste into a placeholder.
export const PASTE_COLLAPSE_CHARS = 800;

const PASTE_TOKEN = /\[Pasted text #(\d+)(?: \+\d+ lines?)?\]/g;

export function pasteToken(id: number, text: string): string {
  const lines = text.split("\n").length;
  return `[Pasted text #${id} +${lines} ${lines === 1 ? "line" : "lines"}]`;
}

export function nextPasteId(files: AttachedFile[]): number {
  return files.reduce((max, f) => (f.type === "paste" ? Math.max(max, f.id) : max), 0) + 1;
}

export function referencedPasteIds(text: string): Set<number> {
  return new Set(Array.from(text.matchAll(PASTE_TOKEN), (m) => Number(m[1])));
}

export function expandPastes(text: string, files: AttachedFile[] | undefined): string {
  if (!files?.some((f) => f.type === "paste")) return text;
  return text.replace(PASTE_TOKEN, (token, id) => {
    const paste = files.find((f): f is PastedText => f.type === "paste" && f.id === Number(id));
    return paste ? paste.content : token;
  });
}

export type PasteSegment = { kind: "text"; text: string } | { kind: "paste"; token: string; paste: PastedText };

export function splitByPastes(text: string, files: AttachedFile[] | undefined): PasteSegment[] {
  const segments: PasteSegment[] = [];
  let cursor = 0;
  for (const match of text.matchAll(PASTE_TOKEN)) {
    const paste = files?.find((f): f is PastedText => f.type === "paste" && f.id === Number(match[1]));
    if (!paste) continue;
    if (match.index > cursor) segments.push({ kind: "text", text: text.slice(cursor, match.index) });
    segments.push({ kind: "paste", token: match[0], paste });
    cursor = match.index + match[0].length;
  }
  if (cursor < text.length) segments.push({ kind: "text", text: text.slice(cursor) });
  return segments;
}

export function tokenEndingAt(text: string, position: number): { start: number; end: number } | null {
  for (const match of text.matchAll(PASTE_TOKEN)) {
    const end = match.index + match[0].length;
    if (end === position) return { start: match.index, end };
  }
  return null;
}
